<template>
    <div v-if="!dismissed" class="feedback-prompt" :class="{ compact, floating }">
        <div v-if="!showQuickFeedback" class="feedback-content">
            <button @click="dismissPrompt" class="btn-dismiss" title="Dismiss">
                <X :size="16" />
            </button>
            <MessageSquare :size="20" class="feedback-icon" />
            <span class="feedback-text">{{ promptText }}</span>
            <div class="feedback-actions">
                <button
                    v-if="allowQuickFeedback"
                    @click="toggleQuickFeedback"
                    class="btn-quick"
                    :title="'Quick feedback'"
                >
                    Quick
                </button>
                <router-link
                    to="/about#contact"
                    class="btn-contact"
                    :title="'Go to contact page'"
                >
                    {{ contactButtonText }}
                </router-link>
            </div>
        </div>

        <!-- Quick Feedback Form -->
        <div v-else class="quick-feedback-form">
            <div class="form-header">
                <h4>Quick Feedback</h4>
                <button @click="closeQuickFeedback" class="close-btn">
                    <X :size="18" />
                </button>
            </div>

            <div v-if="!submitted" class="form-body">
                <!-- Rating -->
                <div class="rating-group">
                    <label>How helpful is this?</label>
                    <div class="rating-buttons">
                        <button
                            @click="rating = true"
                            class="rating-btn"
                            :class="{ active: rating === true }"
                        >
                            <ThumbsUp :size="20" />
                            Helpful
                        </button>
                        <button
                            @click="rating = false"
                            class="rating-btn"
                            :class="{ active: rating === false }"
                        >
                            <ThumbsDown :size="20" />
                            Not Helpful
                        </button>
                    </div>
                </div>

                <!-- Feedback Text -->
                <div class="form-group">
                    <label>Additional Comments (Optional)</label>
                    <textarea
                        v-model="feedbackText"
                        placeholder="Tell us more about your experience..."
                        rows="3"
                        maxlength="500"
                    ></textarea>
                    <span class="char-count">{{ feedbackText.length }}/500</span>
                </div>

                <div class="form-actions">
                    <button @click="submitFeedback" class="btn-submit" :disabled="isSubmitting || rating === null">
                        <Send :size="16" />
                        {{ isSubmitting ? 'Sending...' : 'Send Feedback' }}
                    </button>
                </div>
            </div>

            <!-- Success State -->
            <div v-else class="success-state">
                <CheckCircle :size="32" class="success-icon" />
                <p>Thank you for your feedback!</p>
                <button @click="resetForm" class="btn-reset">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { MessageSquare, ThumbsUp, ThumbsDown, Send, CheckCircle, X } from 'lucide-vue-next';
import api from '@/services/api';

export default {
    name: 'FeedbackPrompt',
    components: {
        MessageSquare,
        ThumbsUp,
        ThumbsDown,
        Send,
        CheckCircle,
        X
    },
    props: {
        promptText: {
            type: String,
            default: 'Have feedback?'
        },
        contactButtonText: {
            type: String,
            default: 'Contact Us'
        },
        allowQuickFeedback: {
            type: Boolean,
            default: true
        },
        compact: {
            type: Boolean,
            default: false
        },
        floating: {
            type: Boolean,
            default: false
        },
        contextData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            showQuickFeedback: false,
            rating: null,
            feedbackText: '',
            isSubmitting: false,
            submitted: false,
            dismissed: false
        };
    },
    methods: {
        dismissPrompt() {
            this.dismissed = true;
            this.$emit('dismissed');
        },
        toggleQuickFeedback() {
            this.showQuickFeedback = !this.showQuickFeedback;
        },
        closeQuickFeedback() {
            if (!this.isSubmitting) {
                this.showQuickFeedback = false;
                this.resetFormData();
            }
        },
        resetFormData() {
            this.rating = null;
            this.feedbackText = '';
            this.submitted = false;
        },
        resetForm() {
            this.showQuickFeedback = false;
            this.resetFormData();
        },
        async submitFeedback() {
            if (this.rating === null) return;

            this.isSubmitting = true;

            try {
                // Submit to API
                const response = await api.submitFeedback({
                    is_helpful: this.rating,
                    feedback_text: this.feedbackText || null,
                    feedback_type: 'general',
                    context: this.contextData
                });

                // Emit event with response data
                this.$emit('feedback-submitted', {
                    rating: this.rating,
                    feedbackText: this.feedbackText,
                    context: this.contextData,
                    timestamp: new Date().toISOString(),
                    response: response.data
                });

                this.submitted = true;

                // Auto-close after 2 seconds
                setTimeout(() => {
                    this.resetForm();
                }, 2000);
            } catch (error) {
                console.error('Error submitting feedback:', error);
                alert('Failed to submit feedback. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>

<style scoped>
.feedback-prompt {
    background-color: var(--bg-secondary, #1a1a1a);
    border: 2px solid rgba(var(--accent-rgb), 0.6);
    padding: 16px 20px;
    transition: all 0.3s ease;
    color: var(--text-primary);
}

.feedback-prompt.compact {
    padding: 12px 16px;
}

.feedback-prompt.floating {
    position: fixed;
    bottom: 95px;
    right: 8px;
    z-index: 9999;
    max-width: 400px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8), 0 0 30px rgba(var(--accent-rgb), 0.2);
    background-color: var(--bg-secondary, #1a1a1a);
    border: 2px solid rgba(var(--accent-rgb), 0.6);
}

.feedback-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.btn-dismiss {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    opacity: 0.6;
    flex-shrink: 0;
}

.btn-dismiss:hover {
    color: var(--accent);
    opacity: 1;
    transform: scale(1.1);
}

.feedback-icon {
    color: var(--accent);
    flex-shrink: 0;
}

.feedback-text {
    color: var(--text-primary);
    font-size: 14px;
    flex: 1;
}

.feedback-actions {
    display: flex;
    gap: 8px;
}

.btn-quick,
.btn-contact {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    background: transparent;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    display: inline-block;
}

.btn-quick:hover,
.btn-contact:hover {
    background: rgba(var(--accent-rgb), 0.2);
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(var(--accent-rgb), 0.3);
}

.btn-contact {
    background: rgba(var(--accent-rgb), 0.8);
    color: var(--bg-primary);
}

.btn-contact:hover {
    background: var(--accent);
    box-shadow: 0 2px 8px rgba(var(--accent-rgb), 0.4);
}

/* Quick Feedback Form */
.quick-feedback-form {
    width: 100%;
}

.form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(var(--accent-rgb), 0.3);
}

.form-header h4 {
    color: var(--text-secondary);
    font-size: 16px;
    margin: 0;
}

.close-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
}

.close-btn:hover {
    color: var(--accent);
    transform: scale(1.1);
}

.form-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.rating-group label,
.form-group label {
    display: block;
    color: var(--text-primary);
    font-size: 13px;
    margin-bottom: 8px;
    font-weight: 500;
}

.rating-buttons {
    display: flex;
    gap: 8px;
}

.rating-btn {
    flex: 1;
    padding: 10px 16px;
    background: transparent;
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    color: var(--text-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    transition: all 0.2s ease;
}

.rating-btn:hover {
    border-color: var(--accent);
    background: rgba(var(--accent-rgb), 0.15);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(var(--accent-rgb), 0.2);
}

.rating-btn.active {
    background: rgba(var(--accent-rgb), 0.2);
    border-color: var(--accent);
}

.form-group {
    position: relative;
}

.form-group textarea {
    width: 100%;
    padding: 10px 12px;
    background: var(--bg-primary);
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    color: var(--text-primary);
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.2s ease;
}

.form-group textarea::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
}

.form-group textarea:focus {
    outline: none;
    border-color: var(--accent);
}

.char-count {
    position: absolute;
    bottom: -18px;
    right: 0;
    font-size: 11px;
    color: var(--text-secondary);
    opacity: 0.7;
}

.form-actions {
    margin-top: 8px;
}

.btn-submit {
    width: 100%;
    padding: 12px 20px;
    background: rgba(var(--accent-rgb), 0.8);
    color: var(--bg-primary);
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
}

.btn-submit:hover:not(:disabled) {
    background: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4);
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Success State */
.success-state {
    text-align: center;
    padding: 20px 0;
}

.success-icon {
    color: #4caf50;
    margin-bottom: 12px;
}

.success-state p {
    color: var(--text-primary);
    margin-bottom: 16px;
}

.btn-reset {
    padding: 8px 24px;
    background: transparent;
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    color: var(--text-primary);
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s ease;
}

.btn-reset:hover {
    border-color: var(--accent);
    background: rgba(var(--accent-rgb), 0.15);
    transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
    .feedback-prompt.floating {
        bottom: 160px;
        right: 10px;
        left: 10px;
        max-width: none;
    }

    .feedback-content {
        flex-wrap: wrap;
    }

    .feedback-text {
        flex: 1 1 100%;
        margin-bottom: 8px;
    }

    .feedback-actions {
        flex: 1 1 100%;
    }

    .btn-quick,
    .btn-contact {
        flex: 1;
    }
}
</style>
